import React, { useEffect, useState } from 'react';
import './App.css';

export default function Directory({ isMember, onLogin, onLogout }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Auth form state
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [authForm, setAuthForm] = useState({ name: '', email: '', password: '' });
  const [form, setForm] = useState({ name: '', phone: '', email: '', social: '' });

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchEntries() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/directory.php', { credentials: 'include' });
      if (res.status === 401) {
        setEntries([]);
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setEntries(data.entries || []);
      setLoading(false);
      // if we got entries, ensure parent knows user is member
      if (onLogin) onLogin();
    } catch (err) {
      setError('Could not load directory');
      setLoading(false);
    }
  }

  const handleAuthChange = (e) => setAuthForm((s) => ({ ...s, [e.target.name]: e.target.value }));
  const handleFormChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  async function submitLogin(e) {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch('/api/login.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authForm.email, password: authForm.password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Login failed');
        return;
      }
      if (onLogin) onLogin();
      await fetchEntries();
    } catch (err) {
      setError('Login failed');
    }
  }

  async function submitRegister(e) {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch('/api/register.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: authForm.name, email: authForm.email, password: authForm.password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Registration failed');
        return;
      }
      // auto-login after register
      await submitLogin(new Event('submit'));
    } catch (err) {
      setError('Registration failed');
    }
  }

  async function submitLogout() {
    try {
      await fetch('/api/logout.php', { method: 'POST', credentials: 'include' });
    } catch {}
    setEntries([]);
    if (onLogout) onLogout();
  }

  async function submitEntry(e) {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch('/api/directory.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to save');
        return;
      }
      setForm({ name: '', phone: '', email: '', social: '' });
      await fetchEntries();
    } catch (err) {
      setError('Failed to save entry');
    }
  }

  // If not authenticated (no entries loaded and not loading), show auth UI
  const notAuthed = !loading && entries.length === 0;

  if (notAuthed) {
    return (
      <section className="directory-page">
        <div className="directory-inner">
          <h2>Directory (Members Only)</h2>
          <p>This directory is for community members. Please log in or register to view and add entries.</p>

          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button className={`btn-primary ${mode === 'login' ? 'active' : ''}`} onClick={() => setMode('login')}>Login</button>
              <button className={`btn-secondary ${mode === 'register' ? 'active' : ''}`} onClick={() => setMode('register')}>Register</button>
            </div>

            {error && <div style={{ color: 'crimson', marginBottom: '0.5rem' }}>{error}</div>}

            {mode === 'login' ? (
              <form onSubmit={submitLogin} className="directory-form">
                <input name="email" placeholder="Email" value={authForm.email} onChange={handleAuthChange} required />
                <input type="password" name="password" placeholder="Password" value={authForm.password} onChange={handleAuthChange} required />
                <button type="submit" className="btn-primary">Log in</button>
              </form>
            ) : (
              <form onSubmit={submitRegister} className="directory-form">
                <input name="name" placeholder="Full name" value={authForm.name} onChange={handleAuthChange} required />
                <input name="email" placeholder="Email" value={authForm.email} onChange={handleAuthChange} required />
                <input type="password" name="password" placeholder="Password (min 6)" value={authForm.password} onChange={handleAuthChange} required />
                <button type="submit" className="btn-primary">Register</button>
              </form>
            )}

          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="directory-page">
      <div className="directory-inner">
        <div className="directory-header">
          <h2>Community Directory</h2>
          <div>
            <button onClick={submitLogout} className="btn-secondary" style={{ marginRight: '0.5rem' }}>Log out</button>
            <button onClick={fetchEntries} className="btn-primary">Refresh</button>
          </div>
        </div>

        {error && <div style={{ color: 'crimson', marginBottom: '0.5rem' }}>{error}</div>}

        <form onSubmit={submitEntry} className="directory-form">
          <input name="name" placeholder="Full name" value={form.name} onChange={handleFormChange} required />
          <input name="phone" placeholder="Phone" value={form.phone} onChange={handleFormChange} />
          <input name="email" placeholder="Email" value={form.email} onChange={handleFormChange} />
          <input name="social" placeholder="Social links (comma separated)" value={form.social} onChange={handleFormChange} />
          <button type="submit" className="btn-primary">Add / Update</button>
        </form>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <ul className="directory-list">
            {entries.map((e) => (
              <li key={e.id} className="directory-item">
                <strong>{e.name}</strong>
                <div className="directory-meta">{e.phone && <span>📞 {e.phone}</span>} {e.email && <span>✉️ {e.email}</span>}</div>
                {e.social && <div className="directory-social">{e.social}</div>}
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.5rem' }}>Added by: {e.owner_name || 'Member'}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
